import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/contexts/AuthContext';
import { useSession } from '@/hooks/useSession';
import { signOut } from '@/lib/auth-actions';
import { getDefaultRouteForRole } from '@/lib/auth-routing';

import { ErrorPage } from './ErrorPage';

// Contextual CTA logic — breaks the loop reported on 2026-05-05.
//
// The original Page403 always linked to /signin. Authenticated users
// reaching it (a buyer landing on /admin, a stale-session admin, or a
// disabled account whose AuthContext flips to anonymous while
// useSession still sees the Supabase session) would click the CTA,
// arrive on /signin where useAuthRedirect bounces signed-in users to
// /dashboard, get re-403'd, and so on.
//
// We resolve the loop with three branches:
//   1. AuthContext authenticated: send the user to their canonical
//      space (/admin or /dashboard). They navigated to a page they
//      shouldn't be on — bouncing them home is the right UX.
//   2. AuthContext anonymous BUT useSession has a session: the profile
//      is missing/disabled/deleted. The only safe path is signOut +
//      navigate /signin so the next page load sees no session at all.
//   3. AuthContext anonymous AND no session: plain link to /signin.
export function Page403() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const { session } = useSession();

  const title = t('system.page403.title');
  const body = t('system.page403.body');

  // Branch 1 — authenticated with a known role: contextual link.
  if (auth.status === 'authenticated') {
    const target = getDefaultRouteForRole(auth.profile.role);
    const label =
      target === '/admin'
        ? t('system.page403.ctaAdmin')
        : target === '/dashboard'
          ? t('system.page403.ctaDashboard')
          : t('system.page403.cta');
    return <ErrorPage code="403" title={title} body={body} ctaLabel={label} ctaTo={target} />;
  }

  // Branch 2 — anonymous per AuthContext but Supabase session exists.
  // Stale / disabled / deleted profile. SignOut + bounce to /signin.
  if (session) {
    const handleSignOut = () => {
      void signOut()
        .catch(() => undefined)
        .finally(() => navigate('/signin', { replace: true }));
    };
    return (
      <ErrorPage
        code="403"
        title={title}
        body={body}
        ctaLabel={t('system.page403.ctaSignOut')}
        onCtaClick={handleSignOut}
      />
    );
  }

  // Branch 3 — truly anonymous. Plain link to /signin.
  return (
    <ErrorPage
      code="403"
      title={title}
      body={body}
      ctaLabel={t('system.page403.cta')}
      ctaTo="/signin"
    />
  );
}
